'use client';

import { BodyStrong } from '../../../components/typography';
import { VStack } from '@terra/ui-styled-system';
import { GeneratedMailboxSection } from '../../application-credentials/GeneratedMailboxSection';
import { useTranslations } from 'next-intl';
import type { CredentialsApiDto } from '@terra/shared-contracts-applications';
import { resolveCredentialMailboxAddress } from '../credential-mailbox';
import { CredentialInboxMessagesSection } from './CredentialInboxMessagesSection';
import { CredentialMfaSection } from './MfaTab';
import { InfoRow } from './InfoRow';
import { ReadOnlyField } from './ReadOnlyField';

type Credential = CredentialsApiDto['credentials'][number];

interface CredentialTabsProps {
  credential: Credential;
}

type BasicInformationTabProps = CredentialTabsProps & {
  applicationId: string;
};

type RequestAuthenticationTabProps = CredentialTabsProps;

const resolveActiveMfaTypes = ({
  credential,
  labels,
}: {
  credential: Credential;
  labels: Record<'EMAIL' | 'SMS' | 'OTP' | 'WEBHOOK', string>;
}): string[] => {
  const types = (credential.mfaConfig ?? [])
    .filter((config) => config.isActive && config.providerType)
    .map((config) =>
      config.providerType ? labels[config.providerType] : undefined,
    )
    .filter((label): label is string => Boolean(label));

  return [...new Set(types)];
};

export const BasicInformationTab = ({
  applicationId,
  credential,
}: BasicInformationTabProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const tCommon = useTranslations('designSystem.common');
  const isEmailType = credential.username?.includes('@');
  const mailboxAddress = resolveCredentialMailboxAddress(credential);
  const mfaTypes = resolveActiveMfaTypes({
    credential,
    labels: {
      EMAIL: t('tabs.mfa.email'),
      SMS: t('tabs.mfa.sms'),
      OTP: t('tabs.mfa.otp'),
      WEBHOOK: t('tabs.mfa.webhook'),
    },
  });
  const isMfaEnabled = mfaTypes.length > 0;

  return (
    <VStack alignItems="stretch" gap={0} w="full">
      <InfoRow
        rows={[
          {
            label: t('tabs.basicInfo.loginType'),
            value: isEmailType
              ? t('tabs.basicInfo.email')
              : t('tabs.basicInfo.username'),
          },
        ]}
      />
      <InfoRow
        rows={[
          {
            label: isEmailType
              ? t('tabs.basicInfo.email')
              : t('tabs.basicInfo.username'),
            value: credential.username,
          },
        ]}
      />
      {mailboxAddress ? (
        <GeneratedMailboxSection emailAddress={mailboxAddress}>
          {credential.id ? (
            <CredentialInboxMessagesSection
              applicationId={applicationId}
              credentialId={credential.id}
            />
          ) : null}
        </GeneratedMailboxSection>
      ) : null}
      <VStack alignItems="stretch" gap={3} py={2.5} w="full">
        <ReadOnlyField
          label={t('tabs.basicInfo.password')}
          value={credential.password || '-'}
          sensitive={Boolean(credential.password)}
          copyable={Boolean(credential.password)}
        />
        <ReadOnlyField
          label={t('tabs.basicInfo.roleDescription')}
          value={credential.roleDescription || '-'}
          multiline={Boolean(credential.roleDescription)}
          copyable={Boolean(credential.roleDescription)}
        />
      </VStack>
      <InfoRow
        rows={[{ label: t('tabs.basicInfo.tenant'), value: credential.tenant }]}
      />
      <InfoRow
        rows={[
          {
            label: t('tabs.mfa.title'),
            value: isMfaEnabled ? tCommon('enabled') : tCommon('disabled'),
          },
        ]}
      />
      {isMfaEnabled ? (
        <>
          <InfoRow
            rows={[
              {
                label: t('tabs.basicInfo.mfaType'),
                value: mfaTypes.join(', '),
              },
            ]}
          />
          <CredentialMfaSection credential={credential} />
        </>
      ) : null}
    </VStack>
  );
};

export const LoginInformationTab = ({ credential }: CredentialTabsProps) => {
  const t = useTranslations('designSystem.credentials.card');

  return (
    <VStack alignItems="stretch" gap={0} w="full">
      <InfoRow
        rows={[
          { label: t('tabs.loginInfo.loginUrl'), value: credential.loginUrl },
          {
            label: t('tabs.loginInfo.ssoProvider'),
            value:
              credential.ssoProvider === 'NONE' ? '-' : credential.ssoProvider,
          },
        ]}
      />
      <InfoRow
        rows={[{ label: t('tabs.loginInfo.maxFailedAuth'), value: null }]}
      />
    </VStack>
  );
};

export const RequestAuthenticationTab = ({
  credential,
}: RequestAuthenticationTabProps) => {
  const t = useTranslations('designSystem.credentials.card');
  const headersDisplay =
    credential.headers?.length > 0
      ? credential.headers.map(({ key }) => key).join(', ')
      : '-';
  const cookiesDisplay =
    credential.cookies?.length > 0
      ? credential.cookies.map(({ name }) => name).join(', ')
      : '-';
  const secretsDisplay =
    credential.secrets?.length > 0
      ? credential.secrets.map(({ key }) => key).join(', ')
      : '-';

  return (
    <VStack alignItems="stretch" gap={4} w="full">
      <VStack alignItems="stretch" gap={0}>
        <BodyStrong pb={2}>{t('tabs.requestAuth.configuredTitle')}</BodyStrong>
        <InfoRow
          rows={[
            { label: t('tabs.requestAuth.headers'), value: headersDisplay },
            { label: t('tabs.requestAuth.secretKey'), value: secretsDisplay },
          ]}
        />
        <InfoRow
          rows={[
            { label: t('tabs.requestAuth.cookies'), value: cookiesDisplay },
          ]}
        />
      </VStack>
    </VStack>
  );
};
